/**
 * Local fleet presence (M6 plan P22, spec §8 "Feed and alerts"): the player corporation's ships in local traffic and
 * its asset alerts on the HUD.
 *
 * - **Own ships in traffic.** The manifest marks a player ship with `LocalAgent.fleet` (P16); the contact store keeps
 *   it as `store.fleet` / `store.own`. The tier step (`traffic/realShips.ts`) promotes own ships first, with their
 *   own ship def, on the slot's team: the player's (corp.json `slot.teamId` 0, `PLAYER_CORP_TEAM_ID`), so player and
 *   corporation fire never hurt each other and no grudge starts.
 * - **The corporation look (hook for P23).** `presenceHook(store)` is the tier env's `onPromoted`: an own ship just
 *   promoted is handed to the bound `CorpLookHook` with its livery colours. Unbound (until P23 lands the livery
 *   uniforms) the ship keeps its def's default look.
 * - **Alerts.** `bindFleetAlerts` subscribes to the client's `onFleetAlert` (each advance's `fleetAlerts`) and puts
 *   every alert in `fleetAlertInbox`, a fixed ring. The Reach HUD reads it with its own cursor into the toast queue
 *   (`hud/reach/toasts.ts readFleetAlertToasts`); a toast keeps its alert so P35 can draw the *jump to* (map focus)
 *   and *send wing* buttons. Those buttons call `runAlertAction`, which goes to the hooks bound with
 *   `bindFleetAlertHooks` (the map and the wing command, wired by their owners).
 *
 * The inbox read is allocation-free when nothing is new; pushing an alert and promoting a ship are message rate.
 */
import { logger } from '../core/logger'
import type { StrategicClient } from '../strategic/client'
import type { FleetAlert, FleetAlertKind, LiveryColors } from '../strategic/types'
import type { ContactStore } from '../traffic/contacts'

// -------------------------------------------------------------------------------------------------
// Own ships in traffic
// -------------------------------------------------------------------------------------------------

/** True when contact `slot` is one of the player corporation's own ships. */
export function isOwnSlot(store: ContactStore, slot: number): boolean {
  return slot >= 0 && slot < store.count && store.own[slot] === 1
}

/** The fleet ship id (`ps-<seq>`) of an own contact, null for any other slot. */
export function ownShipId(store: ContactStore, slot: number): string | null {
  return isOwnSlot(store, slot) ? (store.fleet[slot]?.shipId ?? null) : null
}

/** Own ships flying as real ECS ships now (the HUD and tests count them). Allocation-free. */
export function countOwnReal(store: ContactStore): number {
  let n = 0
  for (let i = 0; i < store.count; i++) if (store.own[i] === 1 && store.eid[i] >= 0) n++
  return n
}

/** Dresses a just-promoted own ship in the corporation look (P23 binds it): its entity, livery and fleet ship id. */
export type CorpLookHook = (eid: number, livery: Readonly<LiveryColors>, shipId: string) => void

/** Puts a just-promoted ship that is not an own ship back in its def's look (its entity id may have been an own ship's). */
export type PlainLookHook = (eid: number) => void

const look: { hook: CorpLookHook | null; plain: PlainLookHook | null } = { hook: null, plain: null }

/**
 * Binds (or, with null, unbinds) the corporation look applied to own real ships (P33 binds it to the ship views'
 * `setLook(eid, 'player_corp')`), and optionally `plain` for every other promotion (bitecs recycles entity ids at once).
 */
export function bindCorpLook(hook: CorpLookHook | null, plain: PlainLookHook | null = null): void {
  look.hook = hook
  look.plain = hook === null ? null : plain
}

/** Applies the corporation look to `slot`'s just-promoted ship `eid` when it is an own ship. Rare (a promotion). */
export function dressPromoted(store: ContactStore, eid: number, slot: number): void {
  const hook = look.hook
  if (hook === null) return
  const fleet = store.own[slot] === 1 ? store.fleet[slot] : null
  if (fleet === null || fleet === undefined) {
    look.plain?.(eid)
    return
  }
  hook(eid, fleet.livery, fleet.shipId)
}

/** The tier env's `onPromoted` for `store` (bound once; the per-promotion call allocates nothing). */
export function presenceHook(store: ContactStore): (eid: number, slot: number) => void {
  return (eid, slot) => dressPromoted(store, eid, slot)
}

// -------------------------------------------------------------------------------------------------
// Alerts
// -------------------------------------------------------------------------------------------------

/** A fleet alert's toast action: focus the map on it, or send the wing (P35 draws the buttons). */
export type FleetAlertAction = 'jumpTo' | 'sendWing'

/** What the alert actions do, bound by their owners (the galaxy map's focus, the wing command). */
export interface FleetAlertHooks {
  jumpTo(alert: Readonly<FleetAlert>): void
  sendWing(alert: Readonly<FleetAlert>): void
}

const hooks: { jumpTo: FleetAlertHooks['jumpTo'] | null; sendWing: FleetAlertHooks['sendWing'] | null } = { jumpTo: null, sendWing: null }

/** Binds the action hooks given (a missing key keeps the current one; null unbinds it). */
export function bindFleetAlertHooks(h: { jumpTo?: FleetAlertHooks['jumpTo'] | null; sendWing?: FleetAlertHooks['sendWing'] | null }): void {
  if (h.jumpTo !== undefined) hooks.jumpTo = h.jumpTo
  if (h.sendWing !== undefined) hooks.sendWing = h.sendWing
}

/**
 * Alerts about something somewhere: *jump to* focuses the map on it. A captain's death is one (D-459 bug 2): a ship lost
 * with its captain raises `captainDied` instead of `shipLost`, so it carries the same place.
 */
const JUMP_KINDS: ReadonlySet<FleetAlertKind> = new Set<FleetAlertKind>(['assetAttacked', 'shipLost', 'captainDied', 'stationBuilt'])
/** Alerts the wing can answer (spec §8: *asset under attack*). */
const WING_KINDS: ReadonlySet<FleetAlertKind> = new Set<FleetAlertKind>(['assetAttacked'])

/** True when the alert offers `action` (*jump to* needs a system; *send wing* only for an asset under attack). */
export function alertOffers(alert: Readonly<FleetAlert>, action: FleetAlertAction): boolean {
  if (action === 'jumpTo') return JUMP_KINDS.has(alert.kind) && alert.systemId !== ''
  return WING_KINDS.has(alert.kind) && alert.systemId !== ''
}

/** Runs a toast action: true when the alert offers it and its hook is bound (then the hook ran). */
export function runAlertAction(alert: Readonly<FleetAlert>, action: FleetAlertAction): boolean {
  if (!alertOffers(alert, action)) return false
  const fn = action === 'jumpTo' ? hooks.jumpTo : hooks.sendWing
  if (fn === null) {
    logger.debug(`fleet-link: no '${action}' hook bound for a ${alert.kind} alert`)
    return false
  }
  fn(alert)
  return true
}

/** The toast tone of an alert kind (losses bad, crew trouble a warning, a finished station good). */
export function fleetAlertTone(kind: FleetAlertKind): 'info' | 'good' | 'warn' | 'bad' {
  switch (kind) {
    case 'assetAttacked':
    case 'shipLost':
    case 'captainDied':
      return 'bad'
    case 'captainDeserted':
    case 'strike':
    case 'wagesLate':
      return 'warn'
    case 'stationBuilt':
      return 'good'
  }
}

/** Alerts the inbox keeps unread (a buffer size: the HUD drains it every frame; older ones are overwritten). */
export const FLEET_ALERT_INBOX = 32

/** A fixed ring of the newest fleet alerts; readers keep their own cursor (`head` counts every alert ever pushed). */
export interface FleetAlertInbox {
  readonly items: (FleetAlert | null)[]
  head: number
}

export function createFleetAlertInbox(capacity = FLEET_ALERT_INBOX): FleetAlertInbox {
  return { items: new Array<FleetAlert | null>(capacity).fill(null), head: 0 }
}

/** The live inbox the Reach HUD reads. */
export const fleetAlertInbox: FleetAlertInbox = createFleetAlertInbox()

/** Adds one alert (message rate). */
export function pushFleetAlert(inbox: FleetAlertInbox, alert: FleetAlert): void {
  inbox.items[inbox.head % inbox.items.length] = alert
  inbox.head++
}

/** The first index a reader at `cursor` can still read (older alerts were overwritten). */
export function firstUnread(inbox: FleetAlertInbox, cursor: number): number {
  const oldest = inbox.head - inbox.items.length
  return cursor > oldest ? cursor : oldest > 0 ? oldest : 0
}

/** The alert at absolute index `i` (between `firstUnread` and `head`). */
export function alertAt(inbox: FleetAlertInbox, i: number): FleetAlert | null {
  return inbox.items[i % inbox.items.length]
}

/** The client call presence makes. */
export type FleetAlertClient = Pick<StrategicClient, 'onFleetAlert'>

const bound: { client: FleetAlertClient | null; off: (() => void) | null } = { client: null, off: null }

/**
 * Routes `client`'s fleet alerts into `inbox` (once per client: a second call with the same client is a no-op, a new
 * client replaces the old subscription). Returns the unsubscribe.
 */
export function bindFleetAlerts(client: FleetAlertClient, inbox: FleetAlertInbox = fleetAlertInbox): () => void {
  if (bound.client === client && bound.off !== null) return bound.off
  bound.off?.()
  const unsubscribe = client.onFleetAlert((a) => pushFleetAlert(inbox, a))
  const off = (): void => {
    unsubscribe()
    if (bound.off === off) {
      bound.client = null
      bound.off = null
    }
  }
  bound.client = client
  bound.off = off
  return off
}
